import { useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useRefreshAgreements } from './useRefreshAgreements';

import { Store } from '@/shared/store';
import { getAgreementsAction, getUserWalletBalanceAction } from '@/shared/store/agreements/actions';
import { ROLE } from '@/shared/api/borrow';
import { useAuthStatus } from '@/shared/hooks/useAuthStatus';

export const useFetchAgreements = (role: ROLE) => {
    const dispatch = useDispatch();
    const refresh = useRefreshAgreements(role);
    const { isLoggedIn } = useAuthStatus();

    const { lenderAgreements, borrowerAgreements, selectedAgreement, agreementsLoading, wallet } =
        useSelector((state: Store) => ({
            lenderAgreements: state.agreements.lenderAgreements,
            borrowerAgreements: state.agreements.borrowerAgreements,
            selectedAgreement: state.agreements.selectedAgreement,
            agreementsLoading: state.agreements.agreementsLoading,
            wallet: state.wallets.active.address,
        }));

    const agreements = useMemo(() => {
        if (role === ROLE.BORROWER) {
            return borrowerAgreements;
        }

        return lenderAgreements;
    }, [role, borrowerAgreements, lenderAgreements]);

    const [error, setError] = useState(false);

    const prevWalletRef = useRef<string | null>(null);
    const hasDispatchedRef = useRef<boolean>(false);

    const shouldFetchWalletBalance = useMemo(() => {
        const prevWallet = prevWalletRef.current;

        const walletChanged = prevWallet !== wallet;

        const isFetchNeeded =
            !agreementsLoading && agreements.length !== 0 && agreements[0].address !== '' && wallet;

        if (isFetchNeeded || walletChanged) {
            return true;
        }

        return false;
    }, [agreementsLoading, agreements]);

    const shouldFetchAgreements = useMemo(() => {
        const prevWallet = prevWalletRef.current;

        const walletChanged = prevWallet !== wallet;

        const isFetchNeeded =
            !agreementsLoading &&
            (agreements.length === 0 || agreements[0].address === '' || selectedAgreement !== role);

        if (isFetchNeeded || walletChanged) {
            hasDispatchedRef.current = false;
            return true;
        }

        return false;
    }, [selectedAgreement, agreements, wallet, role, isLoggedIn]);

    useEffect(() => {
        if (shouldFetchAgreements && !hasDispatchedRef.current && isLoggedIn) {
            const prevWallet = prevWalletRef.current;
            const walletChanged = prevWallet !== wallet;

            dispatch(
                getAgreementsAction({
                    role,
                    withoutCache: walletChanged,
                }),
            );
            hasDispatchedRef.current = true;
        }

        prevWalletRef.current = wallet;
    }, [shouldFetchAgreements, wallet, role, isLoggedIn]);

    useEffect(() => {
        if (shouldFetchWalletBalance && wallet) {
            dispatch(
                getUserWalletBalanceAction({
                    role,
                }),
            );
        }
    }, [wallet, shouldFetchWalletBalance, role, dispatch]);

    useEffect(() => {
        setError(!agreementsLoading && agreements.length === 0);
    }, [agreements, agreementsLoading]);

    return {
        agreements,
        agreementsLoading,
        error,
        refresh,
    };
};
